/**
 * photoQueue.js
 * Armazena blobs de fotos no IndexedDB do dispositivo para envio posterior.
 * Permite que o técnico salve OS com fotos mesmo sem conexão.
 *
 * Estrutura de cada entrada no store:
 *   key         — identificador único (string)
 *   blob        — Blob comprimido da foto
 *   storagePath — caminho destino no Firebase Storage, ex: 'ordens/uid/id/esp_123.jpg'
 *   legenda     — texto da legenda
 *   osId        — ID da OS à qual pertence
 *   createdAt   — timestamp numérico
 */

const DB_NAME    = 'pg_photo_queue';
const DB_VERSION = 1;
const STORE      = 'photos';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'key' });
      }
    };
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror   = (e) => reject(e.target.error);
  });
}

/** Gera uma chave única para a foto */
export function gerarChaveFoto() {
  return `photo_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Salva um blob no IndexedDB. Retorna a key usada. */
export async function enqueuePhoto(key, blob, { storagePath, legenda, osId }) {
  const db = await openDB();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put({ key, blob, storagePath, legenda: legenda || '', osId, createdAt: Date.now() });
    tx.oncomplete = resolve;
    tx.onerror    = (e) => reject(e.target.error);
  });
  db.close();
  return key;
}

/** Lê uma entrada pelo key. */
export async function getQueuedPhoto(key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => { db.close(); resolve(req.result || null); };
    req.onerror   = (e) => { db.close(); reject(e.target.error); };
  });
}

/** Remove uma entrada após upload concluído. */
export async function dequeuePhoto(key) {
  const db = await openDB();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = resolve;
    tx.onerror    = (e) => reject(e.target.error);
  });
  db.close();
}

/** Retorna todas as entradas pendentes. */
export async function getAllQueuedPhotos() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => { db.close(); resolve(req.result || []); };
    req.onerror   = (e) => { db.close(); reject(e.target.error); };
  });
}

/** Retorna fotos pendentes de uma OS específica. */
export async function getQueuedPhotosByOS(osId) {
  const all = await getAllQueuedPhotos();
  return all.filter(p => p.osId === osId);
}
